import React from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import isEmpty from "lodash/isEmpty";

// Material
import { Box, makeStyles } from "@material-ui/core";

// Component
import EventCard from "./card";
import InfoModal from "./modal";
import Form from "./modal/form";
import NoDataPreview from "@/components/shared/noDataPreview";

//Actions
import { showToasterMessage } from "@/store/actions/commonActions";
import { markEvents, registerEventFunc } from "@/store/actions/registeredEventsActions";

// Styles
const useStyles = makeStyles((theme) => ({
    upcomingEventsContainer: {
        width: "100%",
        paddingBottom: theme.spacing(2.25),
    },
    eventsContainer: {
        display: "flex",
        flexWrap: "wrap",
        gap: theme.spacing(2),
        padding: theme.spacing(0, 0, 2),
        "&>:nth-child(n)": {
            padding: theme.spacing(0, 1),
        },
        "&>:last-child": {
            padding: theme.spacing(0, 2, 0, 1),
        },
        "&>:first-child": {
            padding: theme.spacing(0, 1, 0, 0),
        },
    },
    eventCard: {
        width: "100%",
        [theme.breakpoints.down("xs")]: {
            width: "100%",
            minWidth: "unset",
            maxWidth: "unset",
        },
    },
}));

export default function EventsTab({ studentData }) {
    const classes = useStyles();
    const dispatch = useDispatch();

    const registeredEvents = useSelector((state) => state.registeredEvents.data);
    const upcomingEventsState = useSelector((state) => state.upcomingEvents);

    const upcomingEvents = React.useMemo(() => {
        if (registeredEvents.loading || upcomingEventsState.loading) return [];

        const data = upcomingEventsState.data.map((event) => {
            const registeredEvent = registeredEvents[event.cmsId];
            return registeredEvent && registeredEvent.event_start_time
                ? {
                    ...event,
                    dateTimeIsoFormat: moment(
                        registeredEvent.event_start_time
                    ).toISOString(),
                }
                : event;
        });

        data.sort((x, y) =>
            moment(x.dateTimeIsoFormat).isBefore(y.dateTimeIsoFormat)
                ? -1
                : moment(x.dateTimeIsoFormat).isAfter(y.dateTimeIsoFormat)
                    ? 1
                    : 0
        );
        return data;
    }, [registeredEvents, upcomingEventsState]);

    const [eventIndex, setEventIndex] = React.useState(null);
    const [multiDayEventIndex, setMultiDayEventIndex] = React.useState(null);
    const [loadingEventId, setLoadingEventId] = React.useState({});

    const onClickRegisterEvent = (event) => {
        setLoadingEventId((prevState) => ({ ...prevState, [event.cmsId]: true }));
        registerEventFunc(event, studentData, onSuccess);
    };

    const onSuccess = (res, success, data) => {
        if (!(res && success)) {
            dispatch(showToasterMessage("Opps! something went wrong!!", "error"));
        } else {
            dispatch(markEvents(data));
            dispatch(showToasterMessage("Registration Successfull", "success"));
        }
        setLoadingEventId((prevState) => ({ ...prevState, [data.cmsId]: false }));
    };

    return (
        <Box className={classes.upcomingEventsContainer}>
            {upcomingEventsState.loading ? (
                <EventCard loading={true} />
            ) : isEmpty(upcomingEvents) ? (
                <NoDataPreview title={"No events found"} />
            ) : (
                <Box className={classes.eventsContainer}>
                    {upcomingEvents.map((event, index) => (
                        <Box className={classes.eventCard} key={index}>
                            <EventCard
                                desktopCardWidth="100%"
                                coverImage={event.coverImage}
                                dateTimeIsoFormat={event.dateTimeIsoFormat}
                                location={event.location}
                                registrationFee={event.registrationFee}
                                onClickTitle={() => { }}
                                registered={registeredEvents[event.cmsId]}
                                registerButton={{
                                    label: registeredEvents[event.cmsId] ? "Know More" : "Register",
                                    onClick: () => {
                                        if (registeredEvents[event.cmsId]) {
                                            setEventIndex(index);
                                        } else if (event.multiDayFormConfig) {
                                            setMultiDayEventIndex(index);
                                        } else {
                                            onClickRegisterEvent(event, index);
                                        }
                                    },
                                }}
                                title={event.title}
                                loading={loadingEventId[event.cmsId]}
                                eventType={
                                    registeredEvents[event.cmsId]
                                        ? registeredEvents[event.cmsId].event_type ||
                                        registeredEvents[event.cmsId].eventType
                                        : event.eventType
                                }
                                uniqueId={
                                    registeredEvents[event.cmsId]
                                        ? registeredEvents[event.cmsId].unique_id
                                        : ""
                                }
                                slotTime={
                                    registeredEvents[event.cmsId] ? registeredEvents[event.cmsId].slot : ""
                                }
                            />
                        </Box>
                    ))}
                </Box>
            )}
            {eventIndex !== null && (
                <InfoModal
                    open={true}
                    title={upcomingEvents[eventIndex].title}
                    content={upcomingEvents[eventIndex].description}
                    mobileContent={upcomingEvents[eventIndex].mobileDescription}
                    onClose={() => setEventIndex(null)}
                />
            )}
            {multiDayEventIndex !== null && (
                <Form
                    {...upcomingEvents[multiDayEventIndex].multiDayFormConfig}
                    open={true}
                    title={upcomingEvents[multiDayEventIndex].title}
                    onClose={() => setMultiDayEventIndex(null)}
                    onSubmit={(formData) => onClickRegisterEvent({ ...upcomingEvents[multiDayEventIndex], ...formData })}
                />
            )}
        </Box>
    );
}

EventsTab.propTypes = {
    studentData: PropTypes.object.isRequired,
};
